import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { ExternalUserFindDto, type ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import { AccessCardTypeEnum } from '@zaoblako/shared/modules/pacs/enums/access-card-type.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { PacsDtoKeysEnum } from '@zaoblako/shared/modules/pacs/enums/pacs-dto-keys.enum';
import type { ConverterFindInterface } from '@zaoblako/shared/modules/pacs/dtos/converter/converter-find.dto';

import * as yup from 'yup';

/** Access card response body */
export interface AccessCardFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;
  /** Creation date and time */
  created: Date;
  /** Last update date and time */
  updated: Date;
  /** Soft deletion update date and time. The converter will be permanently deleted six months after this date. */
  deleted?: Date;
  /** User who created this converter */
  createdBy?: UserFindInterface;
  /** Card unique code */
  code: string;
  /** Card holder */
  user?: UserFindInterface;
  /** Card holder */
  externalUser?: ExternalUserFindInterface;
  /** Card type */
  type: AccessCardTypeEnum;
  /** Converters where card was added */
  converters?: ConverterFindInterface[];
  /** Number of converters where card was added */
  convertersCount?: number;
  /** Card expiry date */
  begin?: Date;
  end?: Date;
  /** Blocking Card */
  isBlocking?: boolean;
  /** Set active status Card */
  isActive?: boolean;
  /** Maximum number of passes on the card */
  maxPasses?: number;
}

/** Access card response body - DTO schema */
export const AccessCardFindDto = SerdeDtoClass.create<AccessCardFindInterface>(PacsDtoKeysEnum.ACCESS_CARD_FIND_DTO, {
  id: {
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    label: 'Изменен',
    serializer: yup.date(),
  },
  deleted: {
    optional: true,
    label: 'Удален',
    serializer: yup.date(),
  },
  createdBy: {
    optional: true,
    label: 'Автор',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  code: {
    label: 'Код',
    serializer: yup.string(),
  },
  user: {
    optional: true,
    label: 'Пользователь',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  externalUser: {
    optional: true,
    label: 'Внешний пользователь',
    nested: ExternalUserFindDto,
    serializer: (_, nested) => nested,
  },
  type: {
    label: 'Тип карты (протокол)',
    serializer: yup
      .string()
      .oneOf(Object.values(AccessCardTypeEnum)),
  },
  converters: {
    optional: true,
    label: 'Привязка к конвертерам',
    nested: PacsDtoKeysEnum.CONVERTER_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
  convertersCount: {
    optional: true,
    label: 'Количество привязанных карт',
    serializer: yup.number(),
  },
  isBlocking: {
    optional: true,
    label: 'Блокирующая карта',
    serializer: yup.boolean(),
  },
  isActive: {
    optional: true,
    label: 'Активная карта',
    serializer: yup.boolean(),
  },
  maxPasses: {
    optional: true,
    label: 'Максимальное количество проходов',
    serializer: yup.number(),
  },
  begin: {
    optional: true,
    label: 'Начало действия карты',
    serializer: yup.date(),
  },
  end: {
    optional: true,
    label: 'Окончание действия карты',
    serializer: yup.date(),
  },
});
